import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";

/**
 * Reusable utility to export JSON data to an Excel (.xlsx) file.
 */
export const exportToExcel = async (data, options = {}) => {
  if (typeof options === 'string') {
    options = { filename: options };
  }

  const {
    filename = 'export',
    sheetName = 'Sheet1',
    headers,
    onStart,
    onSuccess,
    onError
  } = options;

  try {
    if (onStart) onStart();

    if (!data || data.length === 0) {
      throw new Error('No data available to export');
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    const keys = Object.keys(data[0]);

    // Set columns
    worksheet.columns = keys.map((key, index) => ({
      header: headers ? headers[index] || key : key,
      key: key,
      width: Math.max(String(key).length, 10) + 2
    }));

    // Add rows and check for images
    data.forEach((item, rowIndex) => {
      const row = worksheet.addRow(item);
      const actualRowIndex = rowIndex + 2; // Row 1 is header
      let hasImage = false;

      keys.forEach((key, colIndex) => {
        const val = item[key];
        
        // Handle Base64 Image
        if (typeof val === 'string' && val.startsWith('data:image/')) {
          hasImage = true;
          const base64Data = val.split(',')[1];
          const mimeMatch = val.match(/data:image\/([a-zA-Z0-9]+);base64/);
          let extension = 'png';
          if (mimeMatch && mimeMatch[1]) {
             extension = mimeMatch[1] === 'jpeg' ? 'jpeg' : mimeMatch[1];
          }

          try {
            const imageId = workbook.addImage({
              base64: base64Data,
              extension: extension,
            });

            // Clear the text cell
            row.getCell(colIndex + 1).value = '';

            // Embed the image
            worksheet.addImage(imageId, {
              tl: { col: colIndex, row: actualRowIndex - 1 },
              ext: { width: 80, height: 80 }
            });

            // Set column width to accommodate image (~15 characters wide)
            worksheet.getColumn(colIndex + 1).width = 15;
          } catch (imgErr) {
            console.error("Error embedding image:", imgErr);
          }
        } else {
          // Auto adjust text width up to a max
          const textLength = String(val || '').length;
          const currentWidth = worksheet.getColumn(colIndex + 1).width;
          if (textLength + 2 > currentWidth && textLength < 50) {
             worksheet.getColumn(colIndex + 1).width = textLength + 2;
          }
        }
      });

      // Adjust row height if it contains an image
      if (hasImage) {
        row.height = 70; // Set row height to accommodate the 80px image
      }
    });

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Download file
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\.[^/.]+$/, "")}.xlsx`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);

    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Export Error:', error);
    if (onError) onError(error);
  }
};
//updated Halimunnisa 09-06-2026
// Added exportToPDF utility to allow users to export any DOM element as a PDF, enhancing data sharing and reporting capabilities across the application.
/**
 * Reusable utility to export a DOM element or array of elements to a PDF file.
 */
export const exportToPDF = async (elements, filename = 'document.pdf') => {
  if (!elements) return;
  const elms = Array.isArray(elements) ? elements : [elements];
  if (elms.length === 0) return;

  try {
    // Ensure all custom fonts are completely loaded to prevent text clipping
    await document.fonts.ready;
    
    // Inject a temporary style to prevent specific containers from clipping text bounds
    // We target only .card and .truncate to avoid breaking the top Dashboard Banner's image clipping
    const style = document.createElement('style');
    style.id = 'pdf-export-style';
    style.innerHTML = `
      .card {
        overflow: visible !important;
      }
      .truncate {
        overflow: visible !important;
        white-space: normal !important;
        text-overflow: clip !important;
      }
    `;
    document.head.appendChild(style);

    // Give the browser a moment to apply the styles and font metrics
    await new Promise(resolve => setTimeout(resolve, 500));

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    
    for (let i = 0; i < elms.length; i++) {
      const el = elms[i];
      if (!el) continue;
      
      const canvas = await html2canvas(el, { 
        scale: 2, 
        useCORS: true,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgProps = pdf.getImageProperties(imgData);
      
      // Calculate height proportional to the A4 width
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      if (i > 0) {
        pdf.addPage();
      }
      
      // Draw image
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    }
    
    // Cleanup styles
    document.head.removeChild(style);

    pdf.save(filename);
  } catch (error) {
    console.error('PDF Export Error:', error);
    // Cleanup on error
    const styleNode = document.getElementById('pdf-export-style');
    if (styleNode) document.head.removeChild(styleNode);
  }
};
